import React, { useState, useEffect } from 'react';
import { CogIcon, UserIcon } from '@heroicons/react/24/outline';
import { teachersApi, blockedLessonsApi } from '../services/api';
import toast from 'react-hot-toast';

interface Settings {
  days_in_cycle: number;
  periods_per_day: number;
  school_name: string;
}

interface Teacher {
  code: string;
  name: string;
  surname: string;
  title?: string;
  room?: string;
}

interface BlockedLesson {
  id?: number;
  teacher_code: string;
  day: number;
  period: number;
  reason?: string;
}

const SettingsPage: React.FC = () => {
  const [settings] = useState<Settings>({
    days_in_cycle: 10,
    periods_per_day: 6,
    school_name: 'Northwood School'
  });

  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [selectedTeacher, setSelectedTeacher] = useState<string>('');
  const [blockedLessons, setBlockedLessons] = useState<BlockedLesson[]>([]);
  const [selectedPeriods, setSelectedPeriods] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadTeachers();
    loadBlockedLessons();
  }, []);

  useEffect(() => {
    if (selectedTeacher) {
      loadTeacherBlockedLessons(selectedTeacher);
    }
  }, [selectedTeacher]);

  const loadTeachers = async () => {
    try {
      const response = await teachersApi.getAll();
      setTeachers(response.data);
    } catch (error) {
      console.error('Error loading teachers:', error);
      toast.error('Failed to load teachers');
    }
  };

  const deleteTeacherBlockedLessons = async (teacherCode: string) => {
    if (!window.confirm(`Are you sure you want to delete all blocked lessons for this teacher?`)) {
      return;
    }

    setLoading(true);
    try {
      console.log('Deleting all blocked lessons for teacher:', teacherCode);
      
      // Call the simple delete endpoint
      const response = await blockedLessonsApi.deleteAll(teacherCode);
      console.log('Delete response:', response);
      
      toast.success('Blocked lessons deleted successfully');
      
      // Reload the data
      await loadBlockedLessons();
      
      // If this was the selected teacher, clear the selection and reload their data
      if (selectedTeacher === teacherCode) {
        setSelectedPeriods(new Set());
        await loadTeacherBlockedLessons(teacherCode);
      }
    } catch (error) {
      console.error('Error deleting blocked lessons:', error);
      toast.error('Failed to delete blocked lessons');
    } finally {
      setLoading(false);
    }
  };

  const loadBlockedLessons = async () => {
    try {
      // Add timestamp to prevent caching
      const response = await blockedLessonsApi.getAll();
      console.log('Loaded blocked lessons:', response.data);
      setBlockedLessons(response.data);
    } catch (error) {
      console.error('Error loading blocked lessons:', error);
    }
  };

  const loadTeacherBlockedLessons = async (teacherCode: string) => {
    try {
      const response = await blockedLessonsApi.getAll(teacherCode);
      const blocked = new Set<string>();
      response.data.forEach((lesson: BlockedLesson) => {
        blocked.add(`${lesson.day}-${lesson.period}`);
      });
      setSelectedPeriods(blocked);
    } catch (error) {
      console.error('Error loading teacher blocked lessons:', error);
    }
  };

  const handlePeriodToggle = (day: number, period: number) => {
    const key = `${day}-${period}`;
    const newSelected = new Set(selectedPeriods);
    
    if (newSelected.has(key)) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }
    
    setSelectedPeriods(newSelected);
  };

  const saveBlockedLessons = async () => {
    if (!selectedTeacher) {
      toast.error('Please select a teacher');
      return;
    }

    setLoading(true);
    try {
      const blockedPeriods = Array.from(selectedPeriods).map(key => {
        const [day, period] = key.split('-').map(Number);
        return { day, period, reason: 'Blocked by admin' };
      });

      await blockedLessonsApi.bulkUpdate(selectedTeacher, blockedPeriods);
      toast.success('Blocked lessons saved successfully');
      await loadBlockedLessons();
    } catch (error) {
      console.error('Error saving blocked lessons:', error);
      toast.error('Failed to save blocked lessons');
    } finally {
      setLoading(false);
    }
  };

  const getBlockedLessonsForTeacher = (teacherCode: string) => {
    return blockedLessons.filter(lesson => lesson.teacher_code === teacherCode);
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center space-x-3">
        <CogIcon className="w-8 h-8 text-accent-gold" />
        <h1 className="text-h1 font-montserrat text-northwood-navy">Settings</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column - Timetable Summary */}
        <div className="card">
          <h2 className="text-h3 font-montserrat text-northwood-navy mb-6">
            Timetable Configuration
          </h2>

          <div className="p-4 bg-semantic-info bg-opacity-10 border border-semantic-info rounded-lg">
            <h3 className="font-montserrat font-semibold text-semantic-info mb-2">
              Total Schedule Slots
            </h3>
            <p className="text-sm text-neutral-dark-gray">
              Your timetable will have <strong>{settings.days_in_cycle * settings.periods_per_day}</strong> total slots 
              ({settings.days_in_cycle} days × {settings.periods_per_day} periods)
            </p>
          </div>
        </div>

        {/* Right Column - Teacher Blocked Lessons */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <UserIcon className="w-6 h-6 text-accent-teal" />
            <h2 className="text-h3 font-montserrat text-northwood-navy">
              Teacher Blocked Lessons
            </h2>
          </div>

          <div className="space-y-6">
            {/* Teacher Selection */}
            <div>
              <label className="block text-sm font-roboto font-medium text-neutral-dark-gray mb-2">
                Select Teacher
              </label>
              <select
                value={selectedTeacher}
                onChange={(e) => setSelectedTeacher(e.target.value)}
                className="w-full px-4 py-2 border border-neutral-silver rounded-lg focus:ring-2 focus:ring-accent-teal focus:border-accent-teal"
              >
                <option value="">Choose a teacher...</option>
                {teachers.map(teacher => (
                  <option key={teacher.code} value={teacher.code}>
                    {teacher.name} {teacher.surname} ({teacher.code})
                  </option>
                ))}
              </select>
            </div>

            {/* Period Selection Grid */}
            {selectedTeacher && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-roboto font-medium text-neutral-dark-gray">
                    Blocked Periods
                  </label>
                  <span className="text-sm text-gray-600">
                    {selectedPeriods.size} periods blocked
                  </span>
                </div>
                
                <div className="border border-neutral-silver rounded-lg p-4 max-h-80 overflow-y-auto">
                  <div className={`grid gap-2 text-xs`} style={{ gridTemplateColumns: `repeat(${settings.periods_per_day + 1}, 1fr)` }}>
                    {/* Header Row */}
                    <div className="font-semibold p-2 bg-gray-100 text-center rounded min-h-[36px] flex items-center justify-center">Day/Period</div>
                    {Array.from({length: settings.periods_per_day}, (_, i) => (
                      <div key={i} className="font-semibold p-2 bg-gray-100 text-center rounded min-h-[36px] flex items-center justify-center">
                        P{i + 1}
                      </div>
                    ))}
                    
                    {/* Grid Rows */}
                    {Array.from({length: settings.days_in_cycle}, (_, day) => (
                      <React.Fragment key={day}>
                        <div className="font-semibold p-2 bg-gray-100 text-center rounded min-h-[36px] flex items-center justify-center">
                          Day {day + 1}
                        </div>
                        {Array.from({length: settings.periods_per_day}, (_, period) => {
                          const key = `${day + 1}-${period + 1}`;
                          const isBlocked = selectedPeriods.has(key);
                          
                          return (
                            <button
                              key={period}
                              onClick={() => handlePeriodToggle(day + 1, period + 1)}
                              className={`p-2 text-xs rounded transition-colors min-h-[36px] flex items-center justify-center ${
                                isBlocked 
                                  ? 'bg-red-500 text-white hover:bg-red-600' 
                                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                              }`}
                            >
                              {isBlocked ? '✗' : '○'}
                            </button>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3 mt-4">
                  <button
                    onClick={saveBlockedLessons}
                    disabled={loading}
                    className="btn-primary flex-1"
                  >
                    {loading ? 'Saving...' : 'Save Blocked Lessons'}
                  </button>
                  <button
                    onClick={() => setSelectedPeriods(new Set())}
                    className="btn-outline text-gray-600 border-gray-300 hover:bg-gray-50"
                  >
                    Clear All
                  </button>
                </div>
              </div>
            )}

            {/* Summary Table */}
            <div>
              <h3 className="font-semibold text-neutral-dark-gray mb-3">Blocked Lessons Summary</h3>
              <div className="border border-neutral-silver rounded-lg max-h-60 overflow-y-auto">
                {blockedLessons.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    No blocked lessons configured
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left">Teacher</th>
                        <th className="px-4 py-2 text-left">Blocked Periods</th>
                        <th className="px-4 py-2 text-center">Count</th>
                        <th className="px-4 py-2 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {teachers.map(teacher => {
                        const teacherBlocked = getBlockedLessonsForTeacher(teacher.code);
                        if (teacherBlocked.length === 0) return null;
                        
                        return (
                          <tr key={teacher.code} className="border-t">
                            <td className="px-4 py-2">
                              {teacher.name} {teacher.surname}
                              <div className="text-xs text-gray-500">{teacher.code}</div>
                            </td>
                            <td className="px-4 py-2">
                              <div className="flex flex-wrap gap-1">
                                {teacherBlocked.slice(0, 3).map(lesson => (
                                  <span key={lesson.id} className="inline-block px-2 py-1 bg-red-100 text-red-800 text-xs rounded">
                                    D{lesson.day}P{lesson.period}
                                  </span>
                                ))}
                                {teacherBlocked.length > 3 && (
                                  <span className="inline-block px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                                    +{teacherBlocked.length - 3} more
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-2 text-center font-semibold">
                              {teacherBlocked.length}
                            </td>
                            <td className="px-4 py-2 text-center">
                              <button
                                onClick={() => deleteTeacherBlockedLessons(teacher.code)}
                                disabled={loading}
                                className="text-red-600 hover:text-red-800 hover:bg-red-50 p-2 rounded transition-colors inline-flex items-center justify-center"
                                title="Delete all blocked lessons for this teacher"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                                </svg>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;